import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useMe } from '../api/hooks'
import { ApiError } from '../api/client'
import type { MeResponse } from '../api/types'
import { isKeycloakConfigured, logout } from './keycloak'

export interface AuthState {
  /** Resolved once /api/me has answered. */
  me: MeResponse | null
  /** Correlation id of the /api/me call, for support. */
  correlationId: string | null
  loading: boolean
  error: Error | null
  /** False in stub mode, where there is no session to end. */
  canSignOut: boolean
  signOut: () => void
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const query = useMe()

  const value = useMemo<AuthState>(
    () => ({
      me: query.data?.data ?? null,
      correlationId:
        query.data?.correlationId ??
        (query.error instanceof ApiError ? query.error.correlationId : null),
      loading: query.isPending,
      error: query.error,
      canSignOut: isKeycloakConfigured(),
      signOut: logout,
    }),
    [query.data, query.error, query.isPending],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const state = useContext(AuthContext)
  if (state === null) {
    throw new Error('useAuth must be used inside AuthProvider.')
  }
  return state
}
